import React, { useState, useEffect } from 'react';
import Web3 from 'web3';
import { networks } from '../config/settings'; // Import the network settings
import tokenABI from '../abi/ADBYTES.json'; // Import the token ABI
import Toast from './Toast'; // Import the Toast component
import { CopyToClipboard } from 'react-copy-to-clipboard'; // Import CopyToClipboard

const POLLING_INTERVAL = 10000; // Polling interval in milliseconds (e.g., 10000ms = 10s)

const TokenStats = () => {
  const [tokenName, setTokenName] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [totalSupply, setTotalSupply] = useState('');
  const [currentNetwork, setCurrentNetwork] = useState('');
  const [tokenAddress, setTokenAddress] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    const web3 = window.ethereum ? new Web3(window.ethereum) : null;
    let pollingInterval;

    const loadTokenStats = async () => {
      if (!web3) return;

      const networkId = await web3.eth.net.getId();
      const hexNetworkId = `0x${networkId.toString(16)}`;
      setCurrentNetwork(hexNetworkId);

      const network = Object.values(networks).find(net => net.chainId === hexNetworkId || net.altChainId === hexNetworkId);
      if (!network) {
        console.error('Unsupported network');
        return;
      }

      const useTokenAddress = network.contractAddress;
      setTokenAddress(useTokenAddress);

      // Check if the contract is deployed at the address
      const code = await web3.eth.getCode(useTokenAddress);
      if (code === '0x') {
        console.error(`No contract deployed at address ${useTokenAddress} on network ${hexNetworkId}`);
        return;
      }

      const tokenContract = new web3.eth.Contract(tokenABI, useTokenAddress);

      // Fetch the token name
      try {
        const name = await tokenContract.methods.name().call();
        setTokenName(name);
      } catch (err) {
        console.error('Error calling name():', err.message);
      }

      // Fetch the token symbol
      try {
        const symbol = await tokenContract.methods.symbol().call();
        setTokenSymbol(symbol);
      } catch (err) {
        console.error('Error calling symbol():', err.message);
      }

      // Fetch the total supply and convert to ETH notation
      try {
        const supply = await tokenContract.methods.totalSupply().call();
        setTotalSupply(web3.utils.fromWei(supply, 'ether'));
      } catch (err) {
        console.error('Error calling totalSupply():', err.message);
      }
    };

    if (web3) {
      loadTokenStats();

      // Set up polling interval to refresh the token stats
      pollingInterval = setInterval(loadTokenStats, POLLING_INTERVAL);
    }

    return () => {
      clearInterval(pollingInterval); // Clear the polling interval on component unmount
    };
  }, []);

  const addTokenToWallet = async () => {
    try {
      // Check if MetaMask is installed
      if (window.ethereum) {
        const wasAdded = await window.ethereum.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: {
              address: tokenAddress, // Token contract address
              symbol: tokenSymbol, // Token symbol
              decimals: 18,
              image: null
            },
          },
        });

        if (wasAdded) {
          console.log('Token was added to wallet!');
        } else {
          console.log('Token addition was cancelled.');
        }
      } else {
        console.log('MetaMask is not installed.');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCopy = () => {
    setToastMessage('Copied to Clipboard');
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2000); // Hide the toast after 2 seconds
  };

  const formattedTotalSupply = new Intl.NumberFormat().format(totalSupply);

  const network = Object.values(networks).find(net => net.chainId === currentNetwork);
  console.log(`Current Network: ${network ? network.chainName : 'Unsupported Network'}`); // Add logging to check network details

  return (
    <div className="token-card bg-white shadow-lg rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Token Info</h3>
      {network ? <p className="mb-2 text-gray-600">{network.chainName}</p> : null}
      <p className="mb-2"><strong>Symbol:</strong> {tokenSymbol}</p>
      <p className="mb-4"><strong>Global Supply:</strong> {formattedTotalSupply}</p>
      <button className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 mb-4" onClick={addTokenToWallet}>Add Token to Wallet</button>
      <p className="text-sm">
        Contract Address:
        <CopyToClipboard text={tokenAddress} onCopy={handleCopy}>
          <span className="text-blue-500 cursor-pointer hover:underline"> {tokenAddress}</span>
        </CopyToClipboard>
      </p>
      <Toast message={toastMessage} visible={toastVisible} />
    </div>
  );
};

export default TokenStats;
